"use client";

/**
 * AdminTable — 관리자 공통 표 (TanStack Table 엔진 + Ant Design 스타일 컬럼 API, 우선순위 6)
 *
 * 컬럼은 Ant Design 의 column 과 유사하게 정의한다.
 * - title: 헤더 라벨
 * - dataIndex: 표시할 데이터의 키
 * - render(value, record, index): 셀 커스텀 렌더링 (배지/버튼 등)
 * - width / align / ellipsis: 너비 · 정렬 · 넘침 시 "…" 말줄임
 *
 * 도메인 비종속: 컬럼/데이터는 props 로 주입하고, 정렬·필터·페이징 로직은 프로젝트에서 처리한다.
 * 행 선택 상태는 외부 제어(rowSelection/onRowSelectionChange)하거나 생략 시 내부 상태로 동작한다.
 *
 * @example
 * const columns: AdminColumn<Member>[] = [
 *   { title: "이름", dataIndex: "name" },
 *   { title: "이메일", dataIndex: "email", ellipsis: true },
 *   {
 *     title: "상태", dataIndex: "status", align: "center",
 *     render: (value) => <StatusBadge>{value}</StatusBadge>,
 *   },
 *   {
 *     title: "관리", align: "right",
 *     render: (_v, record) => <Button onClick={() => edit(record)}>수정</Button>,
 *   },
 * ];
 * <AdminTable columns={columns} data={rows} enableRowSelection getRowId={(r) => r.id} />
 *
 * @example // 맨 아래 합계 행(tfoot, 위쪽 구분선만 있음) — 컬럼의 key(또는 dataIndex)를 키로 원하는 컬럼에만 표시
 * <AdminTable
 *   columns={columns}
 *   data={rows}
 *   footer={{
 *     product_name_snapshot: "합계",
 *     total_price: <span className="font-bold">{comma(total)}</span>,
 *   }}
 * />
 *
 * @example // 툴바(요약+액션) + 페이지네이션까지 한 번에 (summary/toolbarActions, total/page/pageSize/onPageChange 로 각각 켜짐)
 * <AdminTable
 *   columns={columns}
 *   data={rows}
 *   summary={<>총 <b>{total}</b>건</>}
 *   toolbarActions={<Button variant="outline">선택 삭제</Button>}
 *   total={total}
 *   page={page}
 *   pageSize={pageSize}
 *   onPageChange={setPage}
 *   onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
 * />
 */
import { type ReactNode, useMemo, useState } from "react";
import {
  type ColumnDef,
  type OnChangeFn,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Loader2,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { type ColumnFormatType, formatByType } from "../../lib/format";
import type { SortType } from "../../hooks/use-page-search-state";
import { Checkbox } from "../ui/checkbox";
import { AdminTableToolbar } from "./admin-table-toolbar";
import { AdminPagination } from "./admin-pagination";

/** Ant Design 스타일 컬럼 정의 */
export interface AdminColumn<TData> {
  /** 헤더 라벨 */
  title: ReactNode;
  /** 표시할 데이터의 키 (없으면 render 로만 렌더링) */
  dataIndex?: keyof TData;
  /** 컬럼 고유 키 (없으면 dataIndex 또는 인덱스로 자동 생성) */
  key?: string;
  /**
   * 값 자동 포맷 종류. (render 가 없을 때 적용)
   * - "comma": 천 단위 콤마 (1234567 → 1,234,567)
   * - "tel": 전화번호 하이픈 (01012345678 → 010-1234-5678)
   * - "bizNum": 사업자등록번호 하이픈 (1234567890 → 123-45-67890)
   */
  type?: ColumnFormatType;
  /** 컬럼 너비 (number=px) */
  width?: number | string;
  /** 셀 정렬 (기본 left) */
  align?: "left" | "center" | "right";
  /** true 이면 헤더 클릭으로 정렬할 수 있다 (서버 정렬: onSortChange 로 전달) */
  sortable?: boolean;
  /** 가로 스크롤 시 좌/우에 고정한다. (width 를 number 로 지정해야 정확히 동작) */
  fixed?: "left" | "right";
  /** true 이면 내용이 넘칠 때 "…" 로 말줄임한다 (width 지정 권장) */
  ellipsis?: boolean;
  /**
   * 셀 커스텀 렌더링. value=해당 셀 값, record=행 데이터, index=행 번호
   * value 타입은 any 다 — 컬럼마다 dataIndex 가 가리키는 필드 타입이 다른데, 이를
   * `TData[keyof TData]` 유니온으로 만들면 TData 에 unknown 타입 필드가 하나만 있어도
   * 유니온 전체가 unknown 으로 붕괴되어(예: address_json?: unknown) 모든 컬럼의 render 에서
   * String(value) 같은 캐스팅을 강제하게 된다. antd 등 다른 테이블 라이브러리와 동일하게 any 로 둔다.
   */
  render?: (value: any, record: TData, index: number) => ReactNode;
  /**
   * 값이 null/undefined/""일 때 대신 보여줄 내용. (render를 쓰면 적용되지 않는다)
   * 컬럼별로 다르게 하고 싶을 때만 지정 — 없으면 AdminTable의 emptyText(기본 "-")를 따른다.
   */
  emptyText?: ReactNode;
  /** 셀(td) 추가 클래스 */
  className?: string;
  /** 헤더(th) 추가 클래스 */
  headerClassName?: string;
}

/** 컬럼 부가 정보를 TanStack meta 로 실어 th/td 에서 읽는다. */
interface ColMeta {
  align?: "left" | "center" | "right";
  sortable?: boolean;
  fixed?: "left" | "right";
  ellipsis?: boolean;
  width?: number | string;
  className?: string;
  headerClassName?: string;
}

/** 너비 값을 px 숫자로 변환 (고정 컬럼 offset 계산용). 알 수 없으면 0 */
function toPx(width: number | string | undefined): number {
  if (typeof width === "number") return width;
  if (typeof width === "string") {
    const n = parseFloat(width);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

export interface AdminTableProps<TData> {
  /** Ant Design 스타일 컬럼 정의 */
  columns: AdminColumn<TData>[];
  data: TData[];
  /** 맨 앞에 체크박스(행 선택) 컬럼을 추가한다 */
  enableRowSelection?: boolean;
  /** 행 선택 상태 (제어 컴포넌트). 생략하면 내부 상태로 동작 */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** 행 고유 id 추출 (선택 상태 키로 사용). 권장. number를 반환해도 내부적으로 문자열로 변환된다 */
  getRowId?: (row: TData, index: number) => string | number;
  /** 로딩 상태 표시 */
  loading?: boolean;
  /** 데이터가 없을 때(행이 0개) 표시할 내용 */
  emptyMessage?: ReactNode;
  /**
   * 셀 값이 null/undefined/""일 때 대신 보여줄 내용. (기본 "-")
   * render를 쓰는 컬럼에는 적용되지 않는다 — 그건 render가 전적으로 책임진다.
   */
  emptyText?: ReactNode;
  /**
   * 정렬 상태 (제어형 서버 정렬). usePageSearchState의 sort와 동일한 타입이라
   * 변환 없이 바로 넘길 수 있다. sortKey가 없으면 정렬 없음.
   */
  sort?: SortType | null;
  /**
   * 정렬 변경 콜백. 오름차순 → 내림차순 → 해제 순으로 토글된다.
   * usePageSearchState의 setSortAndSync에 그대로 연결 가능하다 (예: onSortChange={setSortAndSync}).
   */
  onSortChange?: (sort: SortType) => void;
  /** 행 클릭 콜백 */
  onRowClick?: (row: TData) => void;
  /**
   * 표 본문의 최소 높이를 "행 개수" 기준으로 확보한다.
   * 행이 적거나 비어 있어도 높이가 유지되어 페이지 이동 시 레이아웃이 흔들리지 않는다.
   * (예: minRows={20} → 20행 분량의 최소 높이)
   */
  minRows?: number;
  className?: string;
  /**
   * 표 맨 아래 합계/요약 행(tfoot, 위쪽 구분선만 있음).
   * 컬럼의 key(또는 dataIndex, 없으면 "col_인덱스")를 키로 사용해 원하는 컬럼 자리에만
   * 내용을 넣을 수 있다. 지정하지 않은 컬럼은 빈 셀로 렌더링된다.
   */
  footer?: Record<string, ReactNode>;

  // ── 툴바 (요약 + 액션) — summary 또는 toolbarActions 중 하나라도 있으면 표 위에 표시된다 ──
  /** 툴바 좌측 요약 영역 (예: 총 N건, 선택 N건). 없으면 툴바 자체가 생략된다 */
  summary?: ReactNode;
  /** 툴바 우측 액션 버튼 영역 (등록/삭제/엑셀 다운로드 등) */
  toolbarActions?: ReactNode;
  toolbarClassName?: string;

  // ── 페이지네이션 — total/page/pageSize/onPageChange 가 모두 있으면 표 아래에 표시된다 ──
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  /** 주어지면 "몇 개씩 보기" 선택이 표시된다 */
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  showJump?: boolean;
  paginationClassName?: string;
}

/**
 * rowSelection 객체에서 선택된 행 id 배열을 뽑는다.
 * (TanStack 의 RowSelectionState 는 선택된 행만 키로 가지므로 키 목록이 곧 선택 id 다.)
 *
 * @example
 * const ids = getSelectedRowIds(rowSelection);        // ["3", "7"]
 * const rows = data.filter((d) => rowSelection[d.id]); // 선택된 행 객체
 */
export function getSelectedRowIds(rowSelection: RowSelectionState): string[] {
  return Object.keys(rowSelection).filter((key) => rowSelection[key]);
}

/** 본문 최소 높이 계산용 행 높이 추정치(px) */
const ROW_HEIGHT = 20;
const HEADER_HEIGHT = 44;

const alignClass = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

/** 정렬 상태 아이콘 (오름/내림/없음) */
function SortIcon({ order }: { order?: "ASC" | "DESC" }) {
  if (order === "ASC") return <ChevronUp className="size-3.5" />;
  if (order === "DESC") return <ChevronDown className="size-3.5" />;
  return <ChevronsUpDown className="size-3.5 opacity-40" />;
}

export function AdminTable<TData>({
  columns,
  data,
  enableRowSelection = false,
  rowSelection: controlledSelection,
  onRowSelectionChange: controlledOnChange,
  getRowId,
  loading = false,
  emptyMessage = "데이터가 없습니다.",
  emptyText = "-",
  sort,
  onSortChange,
  onRowClick,
  minRows,
  className,
  footer,
  summary,
  toolbarActions,
  toolbarClassName,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  showJump,
  paginationClassName,
}: AdminTableProps<TData>) {
  const [internalSelection, setInternalSelection] =
    useState<RowSelectionState>({});
  const rowSelection = controlledSelection ?? internalSelection;
  const onRowSelectionChange = controlledOnChange ?? setInternalSelection;

  const hasToolbar = summary != null || toolbarActions != null;
  const hasPagination =
    total !== undefined &&
    page !== undefined &&
    pageSize !== undefined &&
    !!onPageChange;

  // 말줄임(ellipsis) 컬럼이 있거나 fixed(고정) 컬럼이 있으면 table-fixed 로 전환해서
  // width 가 실제 렌더링에 정확히 반영되게 한다. table-layout: auto(기본값)에서는
  // 브라우저가 내용 길이에 따라 컬럼 폭을 자체적으로 줄이기 때문에, fixed 컬럼의 sticky
  // left/right 오프셋 계산(지정한 width 합산)과 실제 렌더링 폭이 어긋나서 고정 컬럼이
  // 엉뚱한 위치에 붙어버린다.
  const hasEllipsis = columns.some((c) => c.ellipsis);
  const hasFixedCol = columns.some((c) => c.fixed);

  // AntD 스타일 컬럼 → TanStack ColumnDef 로 변환한다.
  const finalColumns = useMemo<ColumnDef<TData, unknown>[]>(() => {
    const mapped: ColumnDef<TData, unknown>[] = columns.map((col, i) => ({
      id:
        col.key ??
        (col.dataIndex != null ? String(col.dataIndex) : `col_${i}`),
      header: () => col.title,
      cell: ({ row }) => {
        const value =
          col.dataIndex != null ? row.original[col.dataIndex] : undefined;
        if (col.render) return col.render(value, row.original, row.index);
        const formatted = col.type
          ? formatByType(col.type, value)
          : (value as ReactNode);
        if (formatted === "" || formatted == null) {
          return col.emptyText ?? emptyText;
        }
        return formatted;
      },
      size: typeof col.width === "number" ? col.width : undefined,
      enableSorting: false,
      meta: {
        align: col.align,
        sortable: col.sortable,
        fixed: col.fixed,
        ellipsis: col.ellipsis,
        width: col.width,
        className: col.className,
        headerClassName: col.headerClassName,
      } satisfies ColMeta,
    }));

    if (!enableRowSelection) return mapped;

    // 좌측 고정 컬럼이 있으면 선택 체크박스 컬럼도 함께 좌측 고정한다.
    const hasLeftFixed = columns.some((c) => c.fixed === "left");

    const selectionColumn: ColumnDef<TData, unknown> = {
      id: "__select__",
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? "indeterminate"
                : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="전체 선택"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="행 선택"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      meta: {
        align: "center",
        width: 40,
        fixed: hasLeftFixed ? "left" : undefined,
      } satisfies ColMeta,
    };
    return [selectionColumn, ...mapped];
  }, [columns, enableRowSelection, emptyText]);

  // 고정 컬럼의 sticky left/right offset 을 컬럼 순서/너비로 계산한다.
  const stickyMap = useMemo(() => {
    const map: Record<string, { left?: number; right?: number }> = {};
    let leftAcc = 0;
    for (const col of finalColumns) {
      const meta = col.meta as ColMeta | undefined;
      if (meta?.fixed === "left") {
        map[col.id as string] = { left: leftAcc };
        leftAcc += toPx(meta.width);
      }
    }
    let rightAcc = 0;
    for (let i = finalColumns.length - 1; i >= 0; i--) {
      const col = finalColumns[i];
      const meta = col.meta as ColMeta | undefined;
      if (meta?.fixed === "right") {
        map[col.id as string] = { right: rightAcc };
        rightAcc += toPx(meta.width);
      }
    }
    return map;
  }, [finalColumns]);

  // TanStack Table 의 useReactTable 은 외부 라이브러리 훅이라 react-hooks 규칙 대상에서 제외한다.
  // eslint-disable-next-line react-hooks/incompatible-library
  // TanStack Table의 getRowId는 string만 반환해야 하므로(RowSelectionState가 Record<string, boolean>),
  // number를 반환해도 되도록 여기서 문자열로 변환해준다.
  const table = useReactTable({
    data,
    columns: finalColumns,
    state: { rowSelection },
    enableRowSelection,
    onRowSelectionChange,
    getRowId: getRowId
      ? (row: TData, index: number) => String(getRowId(row, index))
      : undefined,
    getCoreRowModel: getCoreRowModel(),
  });

  const colSpan = finalColumns.length;

  // 헤더 클릭 정렬: 오름차순 → 내림차순 → 해제 토글
  const handleSort = (field: string) => {
    const cur = sort?.sortKey === field ? sort.sortType : undefined;
    const next = cur === "ASC" ? "DESC" : cur === "DESC" ? undefined : "ASC";
    onSortChange?.({ sortKey: next ? field : undefined, sortType: next });
  };

  const tableEl = (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border bg-card text-card-foreground shadow-xs",
        !hasToolbar && !hasPagination && className
      )}
    >
      <div
        className="overflow-x-auto"
        style={
          minRows
            ? { minHeight: HEADER_HEIGHT + minRows * ROW_HEIGHT }
            : undefined
        }
      >
        <table className={cn("w-full text-sm", (hasEllipsis || hasFixedCol) && "table-fixed")}>
          <thead className="border-b bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as
                    | ColMeta
                    | undefined;
                  const sticky = stickyMap[header.column.id];
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        "h-11 px-3 align-middle font-medium text-muted-foreground whitespace-nowrap",
                        alignClass[meta?.align ?? "left"],
                        meta?.ellipsis && "truncate",
                        sticky && "sticky z-20 bg-muted",
                        sticky?.left !== undefined &&
                          "shadow-[2px_0_4px_-2px_rgba(0,0,0,0.12)]",
                        sticky?.right !== undefined &&
                          "shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.12)]",
                        meta?.headerClassName
                      )}
                      style={{
                        width: meta?.width,
                        left: sticky?.left,
                        right: sticky?.right,
                      }}
                      aria-sort={
                        meta?.sortable
                          ? sort?.sortKey === header.column.id
                            ? sort.sortType === "ASC"
                              ? "ascending"
                              : "descending"
                            : "none"
                          : undefined
                      }
                    >
                      {header.isPlaceholder ? null : meta?.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(header.column.id)}
                          className="inline-flex items-center gap-1 select-none transition-colors hover:text-foreground"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <SortIcon
                            order={
                              sort?.sortKey === header.column.id
                                ? sort.sortType
                                : undefined
                            }
                          />
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading || table.getRowModel().rows.length === 0 ? (
              // colSpan td 는 컬럼 폭 합만큼 넓어져서(가로 스크롤 생김) 그 안에서는
              // "현재 보이는 영역" 기준으로 가운데 정렬을 할 수 없다(position:sticky 로도
              // 안 됨 — 이 td 자체가 이미 스크롤 영역 전체 너비라서 뷰포트에 고정되지 않는다).
              // 그래서 내용 없는 빈 placeholder 행으로 높이만 잡아두고, 실제 문구/스피너는
              // 가로 스크롤 영역 밖(아래 relative 컨테이너에 겹쳐진 absolute 오버레이)에서
              // 렌더링해 항상 보이는 영역 기준으로 정확히 가운데에 오게 한다.
              <tr>
                <td colSpan={colSpan} className="h-32" />
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  data-state={row.getIsSelected() ? "selected" : undefined}
                  onClick={
                    onRowClick ? () => onRowClick(row.original) : undefined
                  }
                  className={cn(
                    "group border-b bg-card transition-colors last:border-0 hover:bg-muted data-[state=selected]:bg-muted",
                    onRowClick && "cursor-pointer"
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as
                      | ColMeta
                      | undefined;
                    const sticky = stickyMap[cell.column.id];
                    return (
                      <td
                        key={cell.id}
                        className={cn(
                          "px-3 py-2.5 align-middle",
                          alignClass[meta?.align ?? "left"],
                          meta?.ellipsis ? "truncate" : "whitespace-nowrap",
                          // 고정 셀은 스크롤 내용이 비쳐 보이면 안 되므로 항상 불투명한 배경을 직접 지정한다.
                          // (bg-inherit로 행의 hover 배경을 상속하면 반투명 배경일 때 스크롤 중 호버 시
                          //  뒤에 있는(스크롤된) 셀이 비쳐 보이는 문제가 생긴다. 그래서 group-hover로
                          //  행과 동일한 불투명 색을 별도 지정 — 행의 hover 배경도 완전 불투명이어야
                          //  고정/비고정 컬럼 색이 정확히 일치한다.)
                          sticky &&
                            "sticky z-10 bg-card transition-colors group-hover:bg-muted group-data-[state=selected]:bg-muted",
                          sticky?.left !== undefined &&
                            "shadow-[2px_0_4px_-2px_rgba(0,0,0,0.12)]",
                          sticky?.right !== undefined &&
                            "shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.12)]",
                          meta?.className
                        )}
                        style={{
                          maxWidth: meta?.width,
                          left: sticky?.left,
                          right: sticky?.right,
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
          {footer && (
            <tfoot>
              <tr className="border-t bg-card">
                {finalColumns.map((col) => {
                  const meta = col.meta as ColMeta | undefined;
                  const sticky = stickyMap[col.id as string];
                  return (
                    <td
                      key={col.id}
                      className={cn(
                        "px-3 py-2.5 align-middle",
                        alignClass[meta?.align ?? "left"],
                        sticky && "sticky z-10 bg-card",
                        sticky?.left !== undefined &&
                          "shadow-[2px_0_4px_-2px_rgba(0,0,0,0.12)]",
                        sticky?.right !== undefined &&
                          "shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.12)]",
                        meta?.className
                      )}
                      style={{
                        maxWidth: meta?.width,
                        left: sticky?.left,
                        right: sticky?.right,
                      }}
                    >
                      {footer[col.id as string]}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {(loading || table.getRowModel().rows.length === 0) && (
        <div
          className="absolute inset-x-0 top-11 flex h-32 items-center justify-center text-muted-foreground"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              불러오는 중...
            </span>
          ) : (
            emptyMessage
          )}
        </div>
      )}
    </div>
  );

  if (!hasToolbar && !hasPagination) return tableEl;

  return (
    <div className={cn("space-y-3", className)}>
      {hasToolbar && (
        <AdminTableToolbar
          summary={summary}
          actions={toolbarActions}
          className={toolbarClassName}
        />
      )}
      {tableEl}
      {hasPagination && (
        <AdminPagination
          page={page!}
          pageSize={pageSize!}
          total={total!}
          onPageChange={onPageChange!}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={pageSizeOptions}
          showJump={showJump}
          className={paginationClassName}
        />
      )}
    </div>
  );
}
